import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Building2,
  LogIn,
  UserPlus,
  Shield,
  User,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import {
  useInvitationByToken,
  useAcceptInvitation,
  useRegisterFromOrgInvitation,
} from '@/hooks/useOrganisations';
import { ROUTES } from '@/constants/app';

export const AcceptOrgInvitationPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();

  const {
    data: invitation,
    isLoading,
    error,
  } = useInvitationByToken(token || '');
  const acceptMutation = useAcceptInvitation();
  const registerMutation = useRegisterFromOrgInvitation();

  // Formulaire d'inscription (visiteur non connecté sans compte)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  };

  const handleAccept = async () => {
    if (!token) return;
    try {
      await acceptMutation.mutateAsync(token);
      navigate(ROUTES.ORGANISATIONS);
    } catch {
      // Error handled by mutation
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    try {
      await registerMutation.mutateAsync({
        token,
        data: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          password,
          preferredLanguage: 'fr',
          timezone: getUserTimezone(),
        },
      });
      navigate(ROUTES.ORGANISATIONS);
    } catch {
      // Error handled by mutation
    }
  };

  const roleLabelMap: Record<string, string> = {
    owner: t('organisations.roles.owner', 'Propriétaire'),
    admin: t('organisations.roles.admin', 'Administrateur'),
    member: t('organisations.roles.member', 'Membre'),
    viewer: t('organisations.roles.viewer', 'Lecteur'),
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-600" />
          <p className="text-gray-600">
            {t('common.loading', 'Chargement...')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    const errorMessage =
      (error as HttpApiError)?.response?.data?.message ||
      t(
        'organisations.invitation.notFoundOrExpired',
        'Invitation introuvable ou expirée'
      );

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <XCircle className="mx-auto mb-4 h-16 w-16 text-red-500" />
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            {t('organisations.invitation.invalidTitle', 'Invitation invalide')}
          </h1>
          <p className="mb-6 text-gray-600">{errorMessage}</p>
          <Link
            to={ROUTES.FOLDERS}
            className="inline-block rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
          >
            {t('common.backToHome', "Retour à l'accueil")}
          </Link>
        </div>
      </div>
    );
  }

  // Invitation already accepted
  if (invitation.status === 'accepted') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            {t(
              'organisations.invitation.alreadyAccepted',
              'Invitation déjà acceptée'
            )}
          </h1>
          <p className="mb-6 text-gray-600">
            {t(
              'organisations.invitation.alreadyAcceptedDesc',
              'Vous faites déjà partie de cette organisation.'
            )}
          </p>
          <Link
            to={ROUTES.ORGANISATIONS}
            className="inline-block rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
          >
            {t('organisations.invitation.goToOrg', "Voir l'organisation")}
          </Link>
        </div>
      </div>
    );
  }

  // Invitation expired or cancelled
  if (invitation.status !== 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <XCircle className="mx-auto mb-4 h-16 w-16 text-orange-500" />
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            {t('organisations.invitation.expiredTitle', 'Invitation expirée')}
          </h1>
          <p className="mb-6 text-gray-600">
            {t(
              'organisations.invitation.expiredDesc',
              "Cette invitation n'est plus valide. Contactez l'administrateur de l'organisation."
            )}
          </p>
          <Link
            to={ROUTES.FOLDERS}
            className="inline-block rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
          >
            {t('common.backToHome', "Retour à l'accueil")}
          </Link>
        </div>
      </div>
    );
  }

  // Not authenticated - show login/register options
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-lg">
          <div className="bg-blue-600 p-8 text-center">
            <Building2 className="mx-auto mb-3 h-12 w-12 text-white" />
            <h1 className="mb-1 text-2xl font-bold text-white">
              {t('organisations.invitation.title', 'Invitation à rejoindre')}
            </h1>
            <p className="text-blue-100">{invitation.organisation?.name}</p>
          </div>

          <div className="p-8">
            <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-center">
              <p className="text-sm text-blue-800">
                <strong>
                  {invitation.invitedBy?.firstName}{' '}
                  {invitation.invitedBy?.lastName}
                </strong>{' '}
                {t(
                  'organisations.invitation.invitedYou',
                  'vous invite à rejoindre'
                )}{' '}
                <strong>{invitation.organisation?.name}</strong>{' '}
                {t('organisations.invitation.asRole', 'en tant que')}{' '}
                <span className="font-semibold">
                  {roleLabelMap[invitation.role] || invitation.role}
                </span>
              </p>
            </div>

            <p className="mb-6 text-center text-gray-600">
              {t(
                'organisations.invitation.createAccountPrompt',
                "Créez votre compte pour rejoindre l'organisation."
              )}
            </p>

            <form onSubmit={handleRegister} className="space-y-4">
              {/* Prénom / Nom */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="firstName"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    {t('organisations.invitation.firstName', 'Prénom')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="firstName"
                      type="text"
                      required
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      placeholder={t(
                        'organisations.invitation.firstNamePlaceholder',
                        'Votre prénom'
                      )}
                      className="block w-full rounded-lg border border-gray-300 py-2 pr-3 pl-9 text-sm placeholder:text-gray-400/50 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    {t('organisations.invitation.lastName', 'Nom')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="lastName"
                      type="text"
                      required
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      placeholder={t(
                        'organisations.invitation.lastNamePlaceholder',
                        'Votre nom'
                      )}
                      className="block w-full rounded-lg border border-gray-300 py-2 pr-3 pl-9 text-sm placeholder:text-gray-400/50 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Email (lecture seule, issu de l'invitation) */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-1 block text-xs font-medium text-gray-600"
                >
                  {t('organisations.invitation.email', 'Email')}
                </label>
                <input
                  id="email"
                  type="email"
                  readOnly
                  value={invitation.email}
                  className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                />
              </div>

              {/* Mot de passe */}
              <div>
                <label
                  htmlFor="password"
                  className="mb-1 block text-xs font-medium text-gray-600"
                >
                  {t('organisations.invitation.password', 'Mot de passe')}
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Lock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t(
                      'organisations.invitation.passwordPlaceholder',
                      'Au moins 8 caractères'
                    )}
                    className="block w-full rounded-lg border border-gray-300 py-2 pr-9 pl-9 text-sm placeholder:text-gray-400/50 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={registerMutation.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {registerMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('common.loading', 'Chargement...')}
                  </>
                ) : (
                  <>
                    <UserPlus className="h-5 w-5" />
                    {t(
                      'organisations.invitation.createAccountAndJoin',
                      'Créer mon compte et rejoindre'
                    )}
                  </>
                )}
              </button>
            </form>

            {/* Déjà un compte ? */}
            <div className="mt-5 text-center text-sm">
              <span className="text-gray-500">
                {t(
                  'organisations.invitation.alreadyHaveAccount',
                  'Vous avez déjà un compte ?'
                )}
              </span>{' '}
              <Link
                to={`/login?redirect=/organisations/invitation/${token}`}
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
              >
                <LogIn className="h-4 w-4" />
                {t('auth.login.title', 'Se connecter')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - show accept button
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-lg">
        <div className="bg-blue-600 p-8 text-center">
          <Building2 className="mx-auto mb-3 h-12 w-12 text-white" />
          <h1 className="mb-1 text-2xl font-bold text-white">
            {t('organisations.invitation.title', 'Invitation à rejoindre')}
          </h1>
          <p className="text-blue-100">{invitation.organisation?.name}</p>
        </div>

        <div className="p-8">
          <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-center">
            <p className="text-sm text-blue-800">
              <strong>
                {invitation.invitedBy?.firstName}{' '}
                {invitation.invitedBy?.lastName}
              </strong>{' '}
              {t(
                'organisations.invitation.invitedYou',
                'vous invite à rejoindre'
              )}{' '}
              <strong>{invitation.organisation?.name}</strong>{' '}
              {t('organisations.invitation.asRole', 'en tant que')}{' '}
              <span className="font-semibold">
                {roleLabelMap[invitation.role] || invitation.role}
              </span>
            </p>
          </div>

          {user?.email?.toLowerCase() !== invitation.email.toLowerCase() && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
              <p className="text-sm text-amber-800">
                {t(
                  'organisations.invitation.emailMismatch',
                  'Cette invitation a été envoyée à'
                )}{' '}
                <strong>{invitation.email}</strong>.{' '}
                {t(
                  'organisations.invitation.emailMismatchDesc',
                  "Connectez-vous avec ce compte pour l'accepter."
                )}
              </p>
            </div>
          )}

          <div className="mb-6 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Shield className="h-4 w-4" />
            <span>
              {t(
                'organisations.invitation.connectedAs',
                'Connecté en tant que'
              )}{' '}
              <strong>{user?.email}</strong>
            </span>
          </div>

          <button
            onClick={handleAccept}
            disabled={
              acceptMutation.isPending ||
              user?.email?.toLowerCase() !== invitation.email.toLowerCase()
            }
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {acceptMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('common.loading', 'Chargement...')}
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                {t('organisations.invitation.accept', "Accepter l'invitation")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
